// ============================================
// LIGA AJUPAM - Opener Auth Provider (v6.8.0 · v6.11.208)
// ============================================
// Manejo del token + opener info en state global.
// Patrón análogo a AuthProvider del jugador, pero token-based + sin DNI.
//
// Persistencia: localStorage con key 'opener_session_token' (+ la info de la
// sesión en 'opener_session_info').
//
// v6.11.208 — La mecánica de sesión vive en `useStaffSessionStore`: volver a la
// app ya no desmonta el centro de control y un corte de red ya no desloguea.

import React, { createContext, useCallback, useContext } from 'react';
import {
  openerLogin, validateOpenerSession, openerLogout,
} from '../services/api/openerAuth';
import type { OpenerLoginResult, OpenerSessionInfo } from '../services/api/openerAuth';
import { useStaffSessionStore } from '../hooks/useStaffSessionStore';
import { syncStaffDeviceLabel } from '../services/api/staffDevice';
import { normalizeStaffDeviceLabel, readStaffDeviceLabel } from '../utils/staffDeviceLabel';
import type { StaffSessionConfig } from '../hooks/useStaffSessionStore';

const STORAGE_KEY = 'opener_session_token';
const INFO_KEY = 'opener_session_info';
/** Legacy (≤ v6.11.207): la info de sedes se guardaba aparte. Se sigue leyendo. */
const VENUE_KEY = 'opener_venue_info';

export interface OpenerInfo {
  token: string;
  openerId: string;
  displayName: string;
  leagueId: string;
  leagueSlug: string | null;
  leagueName: string | null;
  expiresAt: string | null;
  defaultVenueId: string | null;
  venueIds: string[];
  venueNames: string[];
}

interface OpenerAuthContextValue {
  opener: OpenerInfo | null;
  /** Sólo el arranque: volver a primer plano NO lo pone en true. */
  isLoading: boolean;
  /** El arranque no pudo validar por falta de red y no había sesión guardada. */
  connectionError: boolean;
  /** Login con username + PIN. Devuelve la info recibida o lanza Error. */
  login: (username: string, pin: string, leagueSlug?: string | null) => Promise<OpenerLoginResult>;
  /** Logout: borra el token + invalida sesión server-side. */
  logout: () => Promise<void>;
  /** Re-validación silenciosa (no desmonta nada). */
  revalidate: () => Promise<void>;
  /** Reintento desde la pantalla de "sin conexión". */
  retry: () => Promise<void>;
}

const OpenerAuthContext = createContext<OpenerAuthContextValue | undefined>(undefined);

const readLegacyVenueInfo = () => {
  try {
    const raw = localStorage.getItem(VENUE_KEY);
    if (raw) return JSON.parse(raw) as { defaultVenueId: string | null; venueIds: string[]; venueNames: string[] };
  } catch { /* noop */ }
  return null;
};

const SESSION_CONFIG: StaffSessionConfig<OpenerInfo, OpenerSessionInfo> = {
  tokenKey: STORAGE_KEY,
  infoKey: INFO_KEY,
  extraKeys: [VENUE_KEY],
  validate: validateOpenerSession,
  merge: (token, v, cached) => {
    const legacy = cached ? null : readLegacyVenueInfo();
    return {
      token,
      openerId: v.opener_id,
      displayName: v.display_name,
      leagueId: v.league_id,
      leagueSlug: cached?.leagueSlug ?? null,
      leagueName: cached?.leagueName ?? null,
      expiresAt: cached?.expiresAt ?? null,
      defaultVenueId: cached?.defaultVenueId ?? legacy?.defaultVenueId ?? null,
      venueIds: cached?.venueIds ?? legacy?.venueIds ?? [],
      venueNames: cached?.venueNames ?? legacy?.venueNames ?? [],
    };
  },
};

export const OpenerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const store = useStaffSessionStore(SESSION_CONFIG);
  const { info: opener, setFromLogin, clear } = store;

  const login = useCallback(async (
    username: string, pin: string, leagueSlug?: string | null,
  ): Promise<OpenerLoginResult> => {
    const result = await openerLogin(username, pin, leagueSlug ?? null);
    setFromLogin({
      token: result.session_token,
      openerId: result.opener_id,
      displayName: result.display_name,
      leagueId: result.league_id,
      leagueSlug: result.league_slug,
      leagueName: result.league_name,
      expiresAt: result.expires_at,
      defaultVenueId: result.default_venue_id ?? null,
      venueIds: result.venue_ids ?? [],
      venueNames: result.venue_names ?? [],
    });
    // v6.11.208 — "¿Quién sos?": el nombre que se guardó en este dispositivo
    // viaja a la sesión (opcional; si falla, el login sigue igual).
    void syncStaffDeviceLabel(result.session_token, normalizeStaffDeviceLabel(readStaffDeviceLabel()));
    return result;
  }, [setFromLogin]);

  const logout = useCallback(async (): Promise<void> => {
    const token = opener?.token;
    if (token) {
      try { await openerLogout(token); } catch { /* noop */ }
    }
    clear();
  }, [opener?.token, clear]);

  return (
    <OpenerAuthContext.Provider value={{
      opener,
      isLoading: store.isLoading,
      connectionError: store.connectionError,
      login,
      logout,
      revalidate: store.revalidate,
      retry: store.retry,
    }}>
      {children}
    </OpenerAuthContext.Provider>
  );
};

export const useOpenerAuth = (): OpenerAuthContextValue => {
  const ctx = useContext(OpenerAuthContext);
  if (!ctx) throw new Error('useOpenerAuth debe usarse dentro de <OpenerAuthProvider>');
  return ctx;
};
